import { findIndex } from "@dhx/ts-common/core";
import { el, KEYED_LIST } from "@dhx/ts-common/dom";
import { getWidth } from "../helpers/cells";
import { removeHTMLTags } from "./../helpers/main";
import { GridEvents, ICol, ICoords, IRendererConfig } from "./../types";
import { getEditor } from "./editors/editors";

type mouseEvents = GridEvents.cellClick
	| GridEvents.cellMouseOver
	| GridEvents.cellMouseDown
	| GridEvents.cellDblClick
	| GridEvents.cellRightClick;

function handleMouse(rowStart: number, colStart: number, conf: IRendererConfig, type: mouseEvents, e: any) {
	// FIXME - move to helpers
	let target = e.target as HTMLElement;
	while (target) {
		if (target.classList && target.classList.contains("dhx_grid-cell")){
			break;
		}
		target = target.parentNode as HTMLElement;
	}

	if (!target){ return; }

	const rowNode = target.parentNode;
	const bodyNode = rowNode.parentNode;

	const colIndex = Array.prototype.indexOf.call(rowNode.childNodes, target);
	const col = conf.columns[colStart+colIndex];

	const rowIndex = Array.prototype.indexOf.call(bodyNode.childNodes, rowNode);
	const row = conf.data[rowStart + rowIndex];

	conf.events.fire(type, [row, col, e]);
}
export function getHandlers(row: number, column: number, conf: IRendererConfig) {
	return {
		onclick: [handleMouse, row, column, conf, GridEvents.cellClick],
		onmouseover: [handleMouse, row, column, conf, GridEvents.cellMouseOver],
		onmousedown: [handleMouse, row, column, conf, GridEvents.cellMouseDown],
		ondblclick: [handleMouse, row, column, conf, GridEvents.cellDblClick],
		oncontextmenu: [handleMouse, row, column, conf, GridEvents.cellRightClick]
	};
}
function getTreeCell(content: any, row: any, col: ICol, conf: IRendererConfig) {
	const isEditable = conf.$editable && conf.$editable.row === row.id && conf.$editable.col === col.id;
	let css = "";

	if (conf.dragMode && conf.dragItem === "row") {
		css += (row.$drophere && !isEditable ? " dhx_grid-cell--drophere" : "") +
			(row.$dragtarget && !isEditable ? " dhx_grid-cell--dragtarget" : "") +
			(!isEditable ? " dhx_grid-cell--drag" : "");
	}

	const parentPadding = 24 * row.$level;
	return el(".dhx_grid-cell", {
		class: `dhx_tree-cell ${col.$cellCss[row.id] || ""} ${row.$items ? "dhx_grid-expand-cell" : ""}` +
			` ${isEditable ? "dhx_tree-editing-cell": ""} ${css}`,
		style: {
			width: col.width,
			lineHeight: conf.rowHeight - 1 + "px",
			padding: !row.$items ? `0 0 0 ${parentPadding}px` : 0
		}
	}, [
			row.$items ? el(".dhx_grid-expand-cell-icon",
				{
					class: row.$opened ? "dxi dxi-chevron-up" : "dxi dxi-chevron-down",
					dhx_id: row.id,
					style: {
						padding: row.$level ? `0 4px 0 ${parentPadding}px` : "0 0 0 4px"
					}
				},
			) : null,
			el(".dhx_tree-cell", {
				title: removeHTMLTags(row[col.id]),
				style: {
					width: "100%",
					height: "100%",
					textAlign: "left"
				}
			}, [content])
		]);
}
function getEditorCell(row: any, col: ICol, conf: IRendererConfig) {
	return getEditor(row, col, conf);
}

export function getCells(conf: IRendererConfig): any[] {
	if (!conf.data || !conf.columns) {
		return [];
	}
	const pos = conf.$positions;
	const data = conf.data ? conf.data.slice(pos.yStart, pos.yEnd) : [];
	const columns = conf.columns.slice(pos.xStart, pos.xEnd);

	return data.map((row, index) => {
		const isLastRow = data.length - 1 === index;
		let rowCss = "";
		if (conf.rowCss) {
			rowCss = conf.rowCss(row);
		}
		if (row.$css) {
			rowCss += row.$css;
		}
		return el(".dhx_grid-row", {
			style: { height: isLastRow ? conf.rowHeight + 1 : conf.rowHeight },
			dhx_id: row.id,
			class: rowCss,
			_key: row.id,
			_flags: KEYED_LIST
		}, columns.map(col => {
			if (!col.hidden) {
				const defaultTemplate = (text: any) => {
					if (typeof text === "boolean" || col.type === "boolean") {
						if (typeof text !== "string") {
							return `${Boolean(text)}`;
						}
					}
					return (text || text === 0) ? text : "";
				};

				let content = col.template ?
					col.template(row[col.id], row, col) :
					defaultTemplate(row[col.id]);

				// content can be a domvm node or a string
				if (typeof content === "string") {
					// QUESTION - do we really need the dhx_cell-content - it doesn't set for number cells anyway
					content = el("div.dhx_cell-content",
						// if user set template or enabled HTML - render as html otherwise as a plain text
						col.template || (conf.htmlEnable && col.htmlEnable !== false) || col.htmlEnable ?
							{ ".innerHTML": content } :
							content
					);
				}

				let css = `${col.$cellCss[row.id] || ""} dhx_${col.type}-cell`.replace(/\s+/g, " ");

				let colWidth = col.width;
				const isEditable = conf.$editable && conf.$editable.row === row.id && conf.$editable.col === col.id;
				if (isEditable || (col.type === "boolean" && (conf.editing || col.editing))) {
					if (!(conf.splitAt && conf.columns.length !== conf.splitAt && conf.columns.indexOf(col) < conf.splitAt)) {
						content = getEditorCell(row, col, conf).toHTML();
						css += " dhx_grid-cell__editable";
						if (conf.splitAt === conf.columns.indexOf(col)+1) {
							colWidth -= 1;
						}
					}
				}

				if (conf.type === "tree" && conf.firstColId === col.id) {
					return getTreeCell(content, row, col, conf);
				}

				let colName;
				if (col.type === "boolean") {
					colName = findIndex(col.header, item => item.text !== undefined);
				}

				if (conf.dragMode && conf.dragItem === "row") {
					css += (row.$drophere && !isEditable ? " dhx_grid-cell--drophere" : "") +
						(row.$dragtarget && !isEditable ? " dhx_grid-cell--dragtarget" : "") +
						(!isEditable ? " dhx_grid-cell--drag" : "");
				}

				return el(".dhx_grid-cell", {
					class: css,
					style: {
						width: colWidth,
						lineHeight: conf.rowHeight - 1 + "px"
					},
					_key: col.id,
					title: col.type === "boolean" ? colName.text : removeHTMLTags(typeof content === "string" ? content : row[col.id])
				}, [content]);
			}
		}));
	});
}

export function getSpans(conf: IRendererConfig): any[] {
	const spanCells = [];
	const columns = conf.columns;

	if (!columns.length) {
		return null;
	}

	if (!conf.spans) {
		return null;
	}
	const spans = conf.spans.sort((a, b) => {
		return (typeof a.row === "string" && typeof b.row === "string") ?
			a.row.localeCompare(b.row)
			: (a.row as number) - (b.row as number);
	});
	const cellHeight = conf.rowHeight;

	for (let i = 0;i < spans.length;i++) {

		const row = spans[i].row;
		const col = spans[i].column;
		const spanHeight = spans[i].rowspan;
		const spanWidth = spans[i].colspan;
		const spanCss = spans[i].css;

		// [todo]
		if (spanHeight === 1) {
			continue;
		}

		const colIndex = findIndex(columns, item => `${item.id}` === `${col}`);
		const rowIndex = findIndex(conf.data, item => `${item.id}` === `${row}`);

		if (colIndex < 0 || rowIndex < 0) {
			continue;
		}

		const currCol = columns[colIndex];
		const currRow = conf.data[rowIndex];

		if (currCol.hidden) {
			continue;
		}

		let content = spans[i].text ?
			spans[i].text
			: currRow[col] === undefined ? "" : currRow[col];

		const tooltipText = content;
		const t = (text: any, _row: any, _col: any) => (text || text === 0) ? text : "";
		const template = currCol.template || t;

		content = template(content, currRow, currCol);
		content = typeof content === "string" ? el("div.dhx_span-cell-content", { ".innerHTML": content }) : content;

		const top = conf.rowHeight * rowIndex - 1;

		let left = 0;
		for (let s = colIndex - 1;s >= 0;s--) {
			left += columns[s].width;
		}
		let css = currCol.header[0].text ? "dhx_span-cell" : "dhx_span-cell dhx_span-cell--title";
		if (spanCss) {
			css += ` ${spanCss}`;
		}

		if (rowIndex === 0) {
			css += " dhx_span-first-row";
		}
		// [dirty]
		if (colIndex === 0) {
			css += " dhx_span-first-col";
		}

		const rowspanWithLastCol = colIndex === columns.length - 1;
		const colspanWithLastCol = colIndex + spanWidth === columns.length;

		if (rowspanWithLastCol || colspanWithLastCol) {
			css += " dhx_span-last-col";
		}

		if (!spanWidth) {
			css += ` dhx_span-${currCol.type || "string"}-cell`;
		} else {
			css += ` dhx_span-string-cell`;
		}

		const width = spanWidth > 1 ? getWidth(conf.columns, spanWidth, colIndex) : currCol.width;

		spanCells.push(
			el("div", {
				class: css,
				style: {
					width,
					height: (spanHeight || 1) * cellHeight,
					top,
					left,
					lineHeight: conf.rowHeight + "px"
				},
				title: removeHTMLTags(tooltipText),
			}, [content])
		);
	}
	return spanCells;
}

export function getShifts(conf: IRendererConfig): ICoords {
	const columnsLeft = conf.columns.slice(0, conf.$positions.xStart);
	return {
		x: columnsLeft.reduce((sum, col) => sum += col.width, 0),
		y: conf.$positions.yStart * conf.rowHeight
	};
}
